import { useState } from 'react';
import { Images, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { apiErrorMessage } from '@/lib/api';
import { GALLERY_DOWNLOAD } from '@/constants/testIds';

const triggerClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] h-10';
const contentClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]';

/** Pick a scope + resolution and download the gallery as a ZIP. */
export const DownloadAllDialog = ({ open, onOpenChange, totalCount, favoriteCount, selectionCount, onDownload }) => {
  const [scope, setScope] = useState('all');
  const [resolution, setResolution] = useState('original');
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    setBusy(true);
    try {
      await onDownload(scope, resolution);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not build your download. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={GALLERY_DOWNLOAD.dialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[hsl(var(--lxra-fg))]">
            <Images className="h-5 w-5 text-[hsl(var(--lxra-gold))]" />
            Download Photos
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Which photos?</p>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid={GALLERY_DOWNLOAD.scopeSelect} className={triggerClass} aria-label="Which photos"><SelectValue /></SelectTrigger>
              <SelectContent className={contentClass}>
                <SelectItem value="all">All Photos ({totalCount})</SelectItem>
                <SelectItem value="favorites">My Favorites ({favoriteCount})</SelectItem>
                <SelectItem value="selections">My Final Picks ({selectionCount})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Resolution</p>
            <Select value={resolution} onValueChange={setResolution}>
              <SelectTrigger data-testid={GALLERY_DOWNLOAD.resolutionSelect} className={triggerClass} aria-label="Resolution"><SelectValue /></SelectTrigger>
              <SelectContent className={contentClass}>
                <SelectItem value="original">Print (Original)</SelectItem>
                <SelectItem value="web">Web</SelectItem>
                <SelectItem value="social">Social</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={handleDownload}
            disabled={busy}
            data-testid={GALLERY_DOWNLOAD.submitButton}
            className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold w-full"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Download ZIP'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
